// Modal de confirmación para iniciar la fase de oferta ciega
import React, { useState } from 'react';
import { AlertTriangle, PlayCircle, EyeOff, Target } from 'lucide-react';
import { DEFAULT_SEMAPHORE_THRESHOLDS } from '../../config/blindBiddingConfig';
import BaseModal from './BaseModal';

const StartBlindBiddingModal = ({ property, onConfirm, onCancel }) => {
  // Estado como string para permitir edición libre (borrar, reescribir)
  const [greenThreshold, setGreenThreshold] = useState(
    String(property.semaphore_green_threshold || DEFAULT_SEMAPHORE_THRESHOLDS.green)
  );
  const [yellowThreshold, setYellowThreshold] = useState(
    String(property.semaphore_yellow_threshold || DEFAULT_SEMAPHORE_THRESHOLDS.yellow)
  );

  const greenVal = parseFloat(greenThreshold) || 0;
  const yellowVal = parseFloat(yellowThreshold) || 0;

  const handleConfirm = () => {
    onConfirm(greenVal, yellowVal);
  };

  // Validar que green < yellow
  const isValidThresholds = greenVal > 0 && yellowVal > 0 && greenVal < yellowVal;

  return (
    <BaseModal
      onClose={onCancel}
      title="Iniciar Fase de Oferta Ciega"
      icon={<EyeOff className="w-6 h-6 text-purple-400" />}
      iconBg="bg-purple-500/20"
      borderColor="border-purple-500/30"
      maxWidth="lg:max-w-2xl"
      responsive
      footer={
        <>
          <button
            onClick={onCancel}
            className="flex-1 bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValidThresholds}
            className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold py-3 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlayCircle className="w-5 h-5" />
            Confirmar Inicio
          </button>
        </>
      }
    >
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-purple-200 mb-2">
            <strong>¿Iniciar fase de oferta ciega?</strong>
          </p>
          <p className="text-xs text-purple-300/80">
            Los invitados habilitados podrán enviar ofertas confidenciales para la propiedad:
          </p>
        </div>
      </div>

      <div className="bg-slate-700/30 rounded-lg p-4">
        <p className="text-white font-semibold text-center">
          {property.title}
        </p>
      </div>

      {/* Configuración de umbrales del semáforo */}
      <div className="space-y-4 bg-slate-900/30 rounded-lg p-4 border border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm font-semibold text-white">
            Configuración del Semáforo de Ofertas
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Umbral verde */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              🟢 Umbral Verde (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={greenThreshold}
              onChange={(e) => setGreenThreshold(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
            />
            <p className="text-xs text-slate-400">
              Ofertas desde el precio de salida hasta este %
            </p>
          </div>

          {/* Umbral amarillo */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              🟡 Umbral Amarillo (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={yellowThreshold}
              onChange={(e) => setYellowThreshold(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-500"
            />
            <p className="text-xs text-slate-400">
              Ofertas entre % del umbral verde y este % son amarillas
            </p>
          </div>
        </div>

        {!isValidThresholds && (
          <p className="text-xs text-red-400">
            ⚠️ El umbral verde debe ser menor que el amarillo
          </p>
        )}

        <div className="pt-2 border-t border-slate-700">
          <div className="flex gap-4 text-xs">
            {/* Ejemplo genérico con 100.000€ */}
            <div className="text-slate-400 space-y-1 flex-1">
              <p className="font-semibold text-slate-300">Ejemplo (100.000€):</p>
              <p>🟢 ≤{greenVal}%: ≥ {(100000 * (1 - greenVal/100)).toLocaleString('es-ES')}€</p>
              <p>🟡 {greenVal}%-{yellowVal}%: {(100000 * (1 - yellowVal/100)).toLocaleString('es-ES')}€ – {(100000 * (1 - greenVal/100)).toLocaleString('es-ES')}€</p>
              <p>🔴 &gt;{yellowVal}%: &lt; {(100000 * (1 - yellowVal/100)).toLocaleString('es-ES')}€</p>
            </div>

            {/* Valores reales basados en el precio original de salida de la propiedad */}
            {(property.originalStartPrice || property.startPrice) && (() => {
              const refPrice = property.originalStartPrice || property.startPrice;
              return (
                <div className="text-slate-400 space-y-1 flex-1">
                  <p className="font-semibold text-slate-300">Esta propiedad ({refPrice.toLocaleString('es-ES')}€):</p>
                  <p className="text-green-400">🟢 ≤{greenVal}%: ≥ {Math.round(refPrice * (1 - greenVal/100)).toLocaleString('es-ES')}€</p>
                  <p className="text-yellow-400">🟡 {greenVal}%-{yellowVal}%: {Math.round(refPrice * (1 - yellowVal/100)).toLocaleString('es-ES')}€ – {Math.round(refPrice * (1 - greenVal/100)).toLocaleString('es-ES')}€</p>
                  <p className="text-red-400">🔴 &gt;{yellowVal}%: &lt; {Math.round(refPrice * (1 - yellowVal/100)).toLocaleString('es-ES')}€</p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="text-sm text-slate-400 space-y-2">
        <p>✅ Los invitados habilitados recibirán una notificación</p>
        <p>✅ Las ofertas serán confidenciales y anónimas</p>
        <p>✅ Podrás seleccionar finalistas al cerrar la fase</p>
      </div>
    </BaseModal>
  );
};

export default StartBlindBiddingModal;
